/* ATHAR — Pre-order hook page (sections/athar-preorder.liquid)
   1. Thermal stage: the visitor's touch or cursor leaves a heat trace on the jacket.
      The activated layer is built from the edition's cold photo (colour-dodge toward
      the edition's activated colour, gated by luminance so the black stage stays black)
      and revealed only where the trace is warm. Traces cool and fade like the real shell.
   2. Edition picker, pre-selected from ?edition=xxx, a returning visitor's choice,
      the visitor's time zone, or their browser region.
   3. Phone (sign-up relay + contact form) or email (customer form) sign-up, then a pass with share links.
      The relay (data-relay) saves the number in Shopify Customers; the contact form still
      sends the inbox message and shows the pass. If the relay is slow or down, the contact
      form goes ahead anyway, so no sign-up is lost.
   4. Returning visitors see the normal page with a link to their pass. */
(function () {
  'use strict';

  var KEY = 'athar-preorder-v1';
  var mql = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  var reduce = !!(mql && mql.matches);

  function load() { try { return JSON.parse(window.localStorage.getItem(KEY) || 'null'); } catch (e) { return null; } }
  function save(v) { try { window.localStorage.setItem(KEY, JSON.stringify(v)); } catch (e) { /* private mode */ } }

  function rgb(hex) {
    var h = String(hex || '#ffffff').replace('#', '');
    if (h.length === 3) h = h.replace(/./g, '$&$&');
    var n = parseInt(h, 16);
    if (isNaN(n)) n = 0xffffff;
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  }

  /* ------------------------------------------------------------------
     Thermal stage
     ------------------------------------------------------------------ */
  function Thermal(o) {
    var stage = o.stage, canvas = o.canvas, media = o.media, img = o.img, base = o.base;
    var shell = o.mode === 'shell' && !!base && !!window.AtharShell;
    var shellCols = null, shellTimer = 0, shellSize = '';
    var ctx = canvas.getContext('2d');
    var mask = document.createElement('canvas');
    var mctx = mask.getContext('2d');
    var hot = document.createElement('canvas');
    var hctx = hot.getContext('2d', { willReadFrequently: true });

    var W = 1, H = 1, DPR = 1, R = 30;
    var fit = { x: 0, y: 0, w: 1, h: 1 };
    var POSX = 0.5, POSY = 0.42;           /* matches object-position in CSS */
    var LIFE = 3400;                       /* ms a trace takes to cool */
    var pts = [];
    var energy = 0;
    var holding = null, last = null;
    var visible = true, ready = false, user = false;
    var ghost = null, lastT = 0, srcKey = '';

    function computeFit() {
      if (shell) {
        fit.x = 0; fit.y = 0; fit.w = W; fit.h = H;
        if (o.onFit) o.onFit(fit);
        return;
      }
      var iw = hot.width || (img && img.naturalWidth) || 16;
      var ih = hot.height || (img && img.naturalHeight) || 9;
      var s = Math.max(W / iw, H / ih);
      fit.w = iw * s; fit.h = ih * s;
      fit.x = (W - fit.w) * POSX; fit.y = (H - fit.h) * POSY;
      if (o.onFit) o.onFit(fit);
    }

    function resize() {
      var r = stage.getBoundingClientRect();
      W = Math.max(1, Math.round(r.width));
      H = Math.max(1, Math.round(r.height));
      DPR = Math.min(window.devicePixelRatio || 1, 1.75);
      canvas.width = mask.width = Math.round(W * DPR);
      canvas.height = mask.height = Math.round(H * DPR);
      R = Math.max(24, Math.min(58, Math.min(W, H) * 0.075));
      computeFit();
      if (shell && shellCols) {
        window.clearTimeout(shellTimer);
        shellTimer = window.setTimeout(drawShell, 140);
      }
    }

    /* Generated shell: render cold + activated from the same geometry at stage size. */
    function drawShell() {
      if (!shellCols) return;
      /* One render pixel per CSS pixel is plenty for a soft gloss and keeps phones fast. */
      var r = Math.min(window.devicePixelRatio || 1, 1.1);
      var w = Math.max(2, Math.round(W * r)), h = Math.max(2, Math.round(H * r));
      var cap = 640000;
      if (w * h > cap) { var k = Math.sqrt(cap / (w * h)); w = Math.round(w * k); h = Math.round(h * k); r *= k; }
      var key = w + 'x' + h + '|' + shellCols[0] + '|' + shellCols[1];
      if (key !== shellSize) {
        shellSize = key;
        var out = window.AtharShell.render(w, h, shellCols[0], shellCols[1], { scale: r, baffles: H > W ? 2.75 : 2.6 });
        base.width = w; base.height = h;
        base.getContext('2d').putImageData(out.cold, 0, 0);
        hot.width = w; hot.height = h;
        hctx.putImageData(out.hot, 0, 0);
      }
      computeFit();
      ready = true;
      media.classList.remove('is-swap');
      media.classList.add('is-ready');
    }
    function setShell(cold, act) {
      var first = !ready;
      shellCols = [cold, act];
      pts = [];
      if (first) { shellTimer = window.setTimeout(drawShell, 30); return; } /* let the page paint first */
      if (reduce) { drawShell(); return; }
      media.classList.add('is-swap');
      window.clearTimeout(shellTimer);
      shellTimer = window.setTimeout(drawShell, 200);
    }

    function buildHot(src, colour) {
      var iw = src.naturalWidth || src.width, ih = src.naturalHeight || src.height;
      var sc = Math.min(1, 1400 / iw);
      var w = Math.max(1, Math.round(iw * sc)), h = Math.max(1, Math.round(ih * sc));
      hot.width = w; hot.height = h;
      hctx.clearRect(0, 0, w, h);
      hctx.drawImage(src, 0, 0, w, h);
      var c = rgb(colour);
      var k0 = 1 / (1 - (c[0] / 255) * 0.9), k1 = 1 / (1 - (c[1] / 255) * 0.9), k2 = 1 / (1 - (c[2] / 255) * 0.9);
      try {
        var d = hctx.getImageData(0, 0, w, h), p = d.data;
        for (var i = 0; i < p.length; i += 4) {
          var r = p[i], g = p[i + 1], b = p[i + 2];
          var L = 0.2126 * r + 0.7152 * g + 0.0722 * b;
          var t = (L - 7) / 22;
          if (t <= 0) { p[i + 3] = 0; continue; }
          if (t > 1) t = 1;
          t = t * t * (3 - 2 * t);
          var nr = r * k0 + c[0] * 0.16, ng = g * k1 + c[1] * 0.16, nb = b * k2 + c[2] * 0.16;
          p[i] = nr > 255 ? 255 : nr;
          p[i + 1] = ng > 255 ? 255 : ng;
          p[i + 2] = nb > 255 ? 255 : nb;
          p[i + 3] = 255 * t;
        }
        hctx.putImageData(d, 0, 0);
      } catch (e) {
        /* Image could not be read (no CORS): plain colour-dodge instead. */
        hctx.globalCompositeOperation = 'color-dodge';
        hctx.fillStyle = colour;
        hctx.fillRect(0, 0, w, h);
        hctx.globalCompositeOperation = 'source-over';
      }
    }

    function emit(x, y, grow, s, add) {
      pts.push({ x: x, y: y, t: performance.now(), r: R * grow, s: s });
      if (pts.length > 320) pts.shift();
      energy = Math.min(1, energy + add);
    }
    function trail(x, y, s, add) {
      if (add == null) add = s * 0.016;
      if (!last) { emit(x, y, 1, s, add); last = { x: x, y: y }; return; }
      var dx = x - last.x, dy = y - last.y, d = Math.sqrt(dx * dx + dy * dy);
      var step = Math.max(4, R * 0.22);
      if (d < step) return;
      var n = Math.min(28, Math.floor(d / step));
      for (var i = 1; i <= n; i++) emit(last.x + dx * i / n, last.y + dy * i / n, 1, s, add);
      last = { x: x, y: y };
    }
    function pos(e) {
      var r = canvas.getBoundingClientRect();
      return { x: e.clientX - r.left, y: e.clientY - r.top };
    }
    function touchedNow() {
      if (!user) { user = true; ghost = null; if (o.onFirst) o.onFirst(); }
    }

    canvas.addEventListener('pointerdown', function (e) {
      touchedNow();
      var p = pos(e);
      last = null;
      trail(p.x, p.y, 0.9);
      holding = { x: p.x, y: p.y, t0: performance.now(), next: 0 };
    });
    canvas.addEventListener('pointermove', function (e) {
      var p = pos(e);
      if (holding) {
        if (Math.abs(p.x - holding.x) + Math.abs(p.y - holding.y) > 3) {
          trail(p.x, p.y, 0.9);
          holding.x = p.x; holding.y = p.y; holding.t0 = performance.now();
        }
      } else if (e.pointerType === 'mouse') {
        touchedNow();
        trail(p.x, p.y, 0.5);
      }
    }, { passive: true });
    function stop() { holding = null; last = null; }
    canvas.addEventListener('pointerup', stop);
    canvas.addEventListener('pointercancel', stop);
    canvas.addEventListener('pointerleave', stop);
    canvas.addEventListener('contextmenu', function (e) { e.preventDefault(); });

    function runGhost(now) {
      if (ghost.t0 === null) ghost.t0 = now + ghost.delay; /* start once the photo is on screen */
      var k = (now - ghost.t0) / ghost.dur;
      if (k < 0) return;
      if (k >= 1) { ghost = null; last = null; if (o.onGhostEnd) o.onGhostEnd(); return; }
      var e = k < 0.5 ? 2 * k * k : 1 - Math.pow(-2 * k + 2, 2) / 2;
      /* a finger stroke down and across the chest, in photo coordinates */
      var u = 0.445 + 0.12 * e + 0.018 * Math.sin(e * Math.PI * 2);
      var v = 0.27 + 0.30 * e;
      trail(fit.x + u * fit.w, fit.y + v * fit.h, 0.95, 0.032);
    }

    function frame(now) {
      window.requestAnimationFrame(frame);
      var dt = lastT ? Math.min(64, now - lastT) : 16;
      lastT = now;
      if (!visible || !ready) return;

      if (ghost) runGhost(now);
      if (holding && now >= holding.next) {
        var grow = 1 + Math.min((now - holding.t0) / 1500, 1) * 1.35;
        emit(holding.x, holding.y, grow, 0.7, 0.042);
        holding.next = now + 55;
      }

      mctx.setTransform(DPR, 0, 0, DPR, 0, 0);
      mctx.clearRect(0, 0, W, H);
      mctx.globalCompositeOperation = 'lighter';
      for (var i = pts.length - 1; i >= 0; i--) {
        var q = pts[i];
        var age = (now - q.t) / LIFE;
        if (age >= 1) { pts.splice(i, 1); continue; }
        var k = 1 - age; k = k * k;
        var a = q.s * k, r = q.r * (1 + 0.45 * age);
        var g = mctx.createRadialGradient(q.x, q.y, 0, q.x, q.y, r);
        g.addColorStop(0, 'rgba(255,255,255,' + a + ')');
        g.addColorStop(0.55, 'rgba(255,255,255,' + (a * 0.6) + ')');
        g.addColorStop(1, 'rgba(255,255,255,0)');
        mctx.fillStyle = g;
        mctx.beginPath();
        mctx.arc(q.x, q.y, r, 0, 6.2832);
        mctx.fill();
      }
      mctx.globalCompositeOperation = 'source-over';

      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      if (pts.length) {
        ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
        ctx.drawImage(hot, fit.x, fit.y, fit.w, fit.h);
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.globalCompositeOperation = 'destination-in';
        ctx.drawImage(mask, 0, 0);
        ctx.globalCompositeOperation = 'source-over';
      }

      energy = Math.max(0, energy - dt * 0.00009);
      if (o.onTemp) o.onTemp(energy, user);
    }

    function setSource(url, colour, done) {
      var key = url + '|' + colour;
      if (key === srcKey) { if (done) done(); return; }
      srcKey = key;
      var first = !ready;
      if (!first) media.classList.add('is-swap');
      function finish(src) {
        if (srcKey !== key) return;
        var apply = function () {
          if (srcKey !== key) return;
          if (img.getAttribute('src') !== url) {
            img.removeAttribute('srcset');
            img.src = url;
          }
          buildHot(src, colour);
          pts = [];
          energy = Math.min(energy, 0.3);
          computeFit();
          ready = true;
          media.classList.remove('is-swap');
          media.classList.add('is-ready');
          if (done) done();
        };
        if (first || reduce) apply(); else window.setTimeout(apply, 220);
      }
      var pre = new Image();
      pre.crossOrigin = 'anonymous';
      pre.onload = function () { finish(pre); };
      pre.onerror = function () {
        /* CDN refused CORS: load without it; the activated layer then uses the plain fallback. */
        var plain = new Image();
        plain.onload = function () { finish(plain); };
        plain.onerror = function () { media.classList.remove('is-swap'); media.classList.add('is-ready'); };
        plain.src = url;
      };
      pre.src = url;
    }

    function playGhost(delay) {
      if (reduce || user) return;
      ghost = { t0: null, delay: delay || 0, dur: 1500 };
      last = null;
    }

    if (window.ResizeObserver) new ResizeObserver(resize).observe(stage);
    else window.addEventListener('resize', resize);
    if (window.IntersectionObserver) {
      new IntersectionObserver(function (en) { visible = en[0].isIntersecting; }, { threshold: 0 }).observe(stage);
    }
    document.addEventListener('visibilitychange', function () { lastT = 0; });
    resize();
    window.requestAnimationFrame(frame);

    return {
      setSource: setSource,
      setShell: setShell,
      isShell: function () { return shell; },
      playGhost: playGhost,
      size: function () { return { w: W, dpr: window.devicePixelRatio || 1 }; },
      fit: function () { return fit; },
      touched: function () { return user; }
    };
  }

  /* ------------------------------------------------------------------
     Page
     ------------------------------------------------------------------ */
  function init(root) {
    if (root.__atp) return;
    root.__atp = true;

    var eds = [];
    try { eds = JSON.parse(root.querySelector('[data-atp-data]').textContent) || []; } catch (e) { eds = []; }
    if (!eds.length) return;

    var $ = function (s) { return root.querySelector(s); };
    var $$ = function (s) { return Array.prototype.slice.call(root.querySelectorAll(s)); };

    var stage = $('[data-atp-stage]'), canvas = $('[data-atp-heat]'), media = $('[data-atp-media]'), img = $('[data-atp-cold]');
    var baseCanvas = $('[data-atp-base]'), mode = root.getAttribute('data-stage') || 'photo';
    var ring = $('[data-atp-ring]'), tag = $('[data-atp-tag]');
    var tempEl = $('[data-atp-temp]'), pin = $('[data-atp-pin]'), stateEl = $('[data-atp-state]');
    var hint = $('[data-atp-hint]');
    var edInputs = $$('[data-atp-ed]'), sizeInputs = $$('[data-atp-size]');
    var phoneForm = $('.atp__form--phone'), emailForm = $('.atp__form--email');
    var cc = $('[data-atp-cc]'), tel = $('[data-atp-tel]'), mail = $('[data-atp-mail]');
    var sw = $('[data-atp-switch]'), join = $('[data-atp-join]'), done = $('[data-atp-done]');
    var dock = $('[data-atp-dock]'), go = $('[data-atp-go]');
    var relay = (root.getAttribute('data-relay') || '').replace(/\/+$/, ''), hp = $('[data-atp-hp]'), relayed = false;
    var tmin = parseFloat(root.getAttribute('data-tmin')); if (isNaN(tmin)) tmin = 20;
    var tmax = parseFloat(root.getAttribute('data-tmax')); if (isNaN(tmax)) tmax = 35;

    var cur = 0, ccTouched = false, heatState = '', hintState = 'pick';

    /* ---------- language: English / Arabic ---------- */
    var AR = null, EN = {}, lang = 'en';
    try { var arEl = $('[data-atp-ar]'); if (arEl) AR = JSON.parse(arEl.textContent); } catch (e) { AR = null; }
    var langBtn = $('[data-atp-lang]');
    $$('[data-i18n]').forEach(function (el) { var k = el.getAttribute('data-i18n'); if (!(k in EN)) EN[k] = el.textContent; });
    $$('[data-i18n-html]').forEach(function (el) { EN[el.getAttribute('data-i18n-html')] = el.innerHTML; });
    $$('[data-i18n-attr]').forEach(function (el) {
      el.getAttribute('data-i18n-attr').split(',').forEach(function (pair) {
        var q = pair.split(':'); if (q.length === 2 && !(q[1] in EN)) EN[q[1]] = el.getAttribute(q[0]) || '';
      });
    });
    EN.share_text = root.getAttribute('data-share-text') || '';
    EN.invalid = 'Enter a valid phone number';
    function t(k) { return (lang === 'ar' && AR && AR[k]) ? AR[k] : (EN[k] != null ? EN[k] : ''); }
    function country(e) { return (lang === 'ar' && e.country_ar) ? e.country_ar : e.country; }
    function colour(e) { return (lang === 'ar' && e.colour_ar) ? e.colour_ar : e.colour; }
    function detectLang() {
      if (!AR) return 'en';
      var m = window.location.search.match(/[?&]lang=(ar|en)\b/i);
      if (m) return m[1].toLowerCase();
      try { var sv = window.localStorage.getItem('athar-lang'); if (sv === 'ar' || sv === 'en') return sv; } catch (e) { /* ignore */ }
      var nl = String((navigator.languages && navigator.languages[0]) || navigator.language || '').toLowerCase();
      if (nl.indexOf('ar') === 0) return 'ar';
      return root.getAttribute('data-lang-default') === 'ar' ? 'ar' : 'en';
    }
    function applyLang(l) {
      lang = (l === 'ar' && AR) ? 'ar' : 'en';
      root.setAttribute('dir', lang === 'ar' ? 'rtl' : 'ltr');
      root.setAttribute('lang', lang);
      try { document.documentElement.setAttribute('lang', lang); } catch (e) { /* ignore */ }
      $$('[data-i18n]').forEach(function (el) { el.textContent = t(el.getAttribute('data-i18n')); });
      $$('[data-i18n-html]').forEach(function (el) { el.innerHTML = t(el.getAttribute('data-i18n-html')); });
      $$('[data-i18n-attr]').forEach(function (el) {
        el.getAttribute('data-i18n-attr').split(',').forEach(function (pair) {
          var q = pair.split(':'); if (q.length === 2) el.setAttribute(q[0], t(q[1]));
        });
      });
      if (langBtn) {
        langBtn.textContent = langBtn.getAttribute(lang === 'ar' ? 'data-ar' : 'data-en');
        langBtn.setAttribute('lang', lang === 'ar' ? 'en' : 'ar');
      }
      if (stateEl && heatState) stateEl.textContent = stateEl.getAttribute('data-' + heatState) || '';
      if (hint) hint.textContent = hintState === 'none' ? '' : (hint.getAttribute(hintState === 'found' ? 'data-found' : 'data-pick') || '');
      if (sw) sw.textContent = sw.getAttribute(root.getAttribute('data-mode') === 'email' ? 'data-to-phone' : 'data-to-email');
      if (typeof thermal !== 'undefined' && eds[cur]) {
        if (tag) tag.textContent = eds[cur].code + (colour(eds[cur]) ? ' · ' + colour(eds[cur]) : '');
        if (root.hasAttribute('data-done')) $('[data-atp-p-ed]').textContent = country(eds[cur]) + (colour(eds[cur]) ? ' · ' + colour(eds[cur]) : '');
      }
    }

    /* ---------- edition ---------- */
    function bySlug(s) {
      s = String(s || '').toLowerCase();
      for (var i = 0; i < eds.length; i++) if (eds[i].slug === s) return i;
      return -1;
    }
    function detect() {
      var m = window.location.search.match(/[?&]edition=([a-z]+)/i);
      if (m && bySlug(m[1]) > -1) return { i: bySlug(m[1]), found: false };
      var st = load();
      if (st && st.code && bySlug(st.code) > -1) return { i: bySlug(st.code), found: false };
      var tz = '';
      try { tz = Intl.DateTimeFormat().resolvedOptions().timeZone || ''; } catch (e) { tz = ''; }
      var i, j;
      if (tz) {
        for (i = 0; i < eds.length; i++) {
          var zs = String(eds[i].tz || '').split(',');
          for (j = 0; j < zs.length; j++) if (zs[j].trim() && zs[j].trim() === tz) return { i: i, found: true };
        }
      }
      var langs = navigator.languages || [navigator.language || ''];
      for (j = 0; j < langs.length; j++) {
        var reg = (String(langs[j]).match(/-([a-z]{2})\b/i) || [])[1];
        if (!reg) continue;
        for (i = 0; i < eds.length; i++) if (String(eds[i].region || '').toUpperCase() === reg.toUpperCase()) return { i: i, found: true };
      }
      return null;
    }

    function setDial(dial) {
      if (!cc || ccTouched || !dial) return;
      for (var i = 0; i < cc.options.length; i++) {
        if (cc.options[i].value === dial) { cc.selectedIndex = i; return; }
      }
    }

    function pickUrl(e) {
      var s = thermal.size();
      return (s.w * Math.min(s.dpr, 2) > 1100) ? (e.imgL || e.img) : e.img;
    }

    function setEdition(i) {
      if (i < 0 || i >= eds.length) return;
      cur = i;
      var e = eds[i];
      root.style.setProperty('--c0', e.c0 || '#1F2A17');
      root.style.setProperty('--c1', e.c1 || '#A8C94F');
      if (tag) tag.textContent = e.code + (colour(e) ? ' · ' + colour(e) : '');
      if (edInputs[i]) edInputs[i].checked = true;
      setDial(e.dial);
      if (thermal.isShell()) thermal.setShell(e.c0 || '#1F2A17', e.c1 || '#A8C94F');
      else if (e.img) thermal.setSource(pickUrl(e), e.c1 || '#A8C94F');
      if (img && e.alt) img.alt = e.alt;
    }

    /* ---------- temperature readout ---------- */
    function onTemp(en, user) {
      var t = (tmin + (tmax - tmin) * en).toFixed(1);
      if (tempEl.textContent !== t) tempEl.textContent = t;
      if (pin) pin.style.left = (4 + en * 92) + '%';
      var s = !user && en < 0.05 ? 'prompt' : en >= 0.72 ? 'hot' : en > 0.12 ? 'warm' : 'cold';
      if (s !== heatState) {
        if (s === 'hot' && user && navigator.vibrate) { try { navigator.vibrate(14); } catch (er) { /* ignore */ } }
        heatState = s;
        root.setAttribute('data-heat', s);
        stateEl.textContent = stateEl.getAttribute('data-' + s) || '';
      }
    }

    var thermal = Thermal({
      stage: stage, canvas: canvas, media: media, img: img, base: baseCanvas, mode: mode,
      onTemp: onTemp,
      onFirst: function () { if (ring) ring.classList.remove('is-on'); },
      onGhostEnd: function () { if (ring && !thermal.touched()) ring.classList.add('is-on'); },
      onFit: function (f) {
        if (!ring) return;
        ring.style.left = (f.x + 0.5 * f.w) + 'px';
        ring.style.top = (f.y + 0.47 * f.h) + 'px';
      }
    });

    var found = detect();
    hintState = found && found.found ? 'found' : 'pick';
    if (hint) hint.classList.toggle('is-found', hintState === 'found');
    applyLang(detectLang());
    setEdition(found ? found.i : 0);
    if (langBtn) langBtn.addEventListener('click', function () {
      var next = lang === 'ar' ? 'en' : 'ar';
      try { window.localStorage.setItem('athar-lang', next); } catch (e) { /* ignore */ }
      applyLang(next);
    });
    thermal.playGhost(700);
    if (reduce && ring) ring.classList.add('is-on');

    edInputs.forEach(function (inp, i) {
      inp.addEventListener('change', function () {
        if (inp.checked) {
          setEdition(i);
          hintState = 'none';
          if (hint) { hint.classList.remove('is-found'); hint.textContent = ''; }
        }
      });
    });
    if (cc) cc.addEventListener('change', function () { ccTouched = true; });

    function size() {
      for (var i = 0; i < sizeInputs.length; i++) if (sizeInputs[i].checked) return sizeInputs[i].value;
      return '';
    }

    /* ---------- mode: phone / email ---------- */
    function setMode(m) {
      root.setAttribute('data-mode', m);
      if (sw) sw.textContent = sw.getAttribute(m === 'email' ? 'data-to-phone' : 'data-to-email');
    }
    setMode('phone');
    if (sw) sw.addEventListener('click', function () {
      var m = root.getAttribute('data-mode') === 'email' ? 'phone' : 'email';
      setMode(m);
      var f = m === 'email' ? mail : tel;
      if (f) f.focus();
    });

    /* ---------- submit ---------- */
    if (phoneForm && tel) {
      tel.addEventListener('input', function () { tel.setCustomValidity(''); tel.removeAttribute('aria-invalid'); });
      phoneForm.addEventListener('submit', function (ev) {
        if (relayed) return; /* second pass after the relay: let the contact form go */
        var code = cc ? cc.value.replace(/[^0-9]/g, '') : '';
        var d = tel.value.replace(/[^0-9]/g, '');
        if (code && d.indexOf('00' + code) === 0) d = d.slice(2 + code.length);
        else if (code && d.indexOf(code) === 0 && d.length > code.length + 7) d = d.slice(code.length);
        d = d.replace(/^0+/, '');
        if (d.length < 6 || d.length > 14) {
          ev.preventDefault();
          tel.setCustomValidity(t('invalid'));
          tel.reportValidity();
          return;
        }
        var e = eds[cur], s = size(), full = '+' + code + ' ' + d;
        phoneForm.querySelector('[data-atp-f-full]').value = full;
        phoneForm.querySelector('[data-atp-f-ed]').value = e.code + ' · ' + e.country;
        phoneForm.querySelector('[data-atp-f-size]').value = s || '—';
        phoneForm.querySelector('[data-atp-f-body]').value =
          'Pre-order list sign-up · ' + e.code + ' (' + e.country + ') · size ' + (s || '—') + ' · ' + full + ' · language ' + lang.toUpperCase();
        var lf = phoneForm.querySelector('[data-atp-f-lang]'); if (lf) lf.value = lang === 'ar' ? 'Arabic' : 'English';
        var cf = phoneForm.querySelector('[data-atp-f-consent]');
        var cl = phoneForm.querySelector('.atp__agree span');
        if (cf) cf.value = 'Yes · ' + new Date().toISOString() + ' · "' + (cl ? cl.textContent.trim() : '') + '"';
        save({ pending: true, mode: 'phone', code: e.slug, size: s, contact: '+' + code + ' •••• ' + d.slice(-4), ts: Date.now() });
        if (!/^https:\/\//.test(relay) || !window.fetch) return;

        /* Save the number in Shopify Customers first, then continue with the contact form. */
        ev.preventDefault();
        relayed = true;
        var btn = phoneForm.querySelector('button[type="submit"]');
        if (btn) { btn.style.pointerEvents = 'none'; btn.style.opacity = '0.7'; btn.setAttribute('aria-busy', 'true'); }
        var sent = false;
        var proceed = function () {
          if (sent) return;
          sent = true;
          if (phoneForm.requestSubmit) phoneForm.requestSubmit();
          else HTMLFormElement.prototype.submit.call(phoneForm);
        };
        var timer = window.setTimeout(proceed, 5000);
        try {
          window.fetch(relay + '/signup', {
            method: 'POST',
            mode: 'cors',
            credentials: 'omit',
            keepalive: true,
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              phone: '+' + code + d, edition: e.slug, size: s, lang: lang,
              consent: cf ? cf.value : '', source: root.getAttribute('data-source') || 'pre-order page', hp: hp ? hp.value : ''
            })
          }).then(function () {}, function () {}).then(function () { window.clearTimeout(timer); proceed(); });
        } catch (er) {
          window.clearTimeout(timer);
          proceed();
        }
      });
    }
    if (emailForm && mail) {
      emailForm.addEventListener('submit', function () {
        var e = eds[cur], s = size();
        var tags = emailForm.querySelector('[data-atp-f-tags]');
        var base = tags.getAttribute('data-base') || '';
        var list = [];
        if (base) list.push(base);
        list.push('edition-' + e.slug);
        list.push('lang-' + lang);
        if (s) list.push('size-' + s.toLowerCase());
        tags.value = list.join(', ');
        var v = mail.value.trim(), at = v.indexOf('@');
        var masked = at > 0 ? v.charAt(0) + '•••' + v.slice(at) : v;
        save({ pending: true, mode: 'email', code: e.slug, size: s, contact: masked, ts: Date.now() });
      });
    }

    /* ---------- done: the pass ---------- */
    function fmtDate(ts) {
      try { return new Date(ts).toLocaleDateString(lang === 'ar' ? 'ar-OM-u-nu-latn' : 'en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); }
      catch (e) { return ''; }
    }
    function shareLink() {
      var base = root.getAttribute('data-share-url') || window.location.origin;
      return base + (base.indexOf('?') > -1 ? '&' : '?') + 'edition=' + eds[cur].slug;
    }
    function showDone(entry, fresh) {
      var i = entry && entry.code ? bySlug(entry.code) : -1;
      if (i > -1 && i !== cur) setEdition(i);
      var e = eds[cur];
      root.setAttribute('data-done', '');
      done.hidden = false;
      $('[data-atp-p-code]').textContent = e.code;
      $('[data-atp-p-ed]').textContent = country(e) + (colour(e) ? ' · ' + colour(e) : '');
      var sz = entry && entry.size;
      $('[data-atp-p-size]').textContent = sz || '';
      $('[data-atp-p-size-row]').hidden = !sz;
      $('[data-atp-p-contact]').textContent = (entry && entry.contact) || '—';
      $('[data-atp-p-date]').textContent = fmtDate((entry && entry.ts) || Date.now());
      updateDock();
      if (fresh) {
        window.setTimeout(function () {
          done.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: window.innerWidth < 900 ? 'start' : 'center' });
          var h = $('[data-atp-done-h]');
          if (h) h.focus({ preventScroll: true });
        }, 250);
      }
    }
    function hideDone() {
      root.removeAttribute('data-done');
      done.hidden = true;
      updateDock();
    }

    var ok = $('[data-atp-ok]'), err = $('[data-atp-err]');
    var entry = load();
    if (ok) {
      if (!entry) entry = { mode: ok.getAttribute('data-atp-ok'), code: eds[cur].slug, ts: Date.now() };
      entry.pending = false;
      save(entry);
      showDone(entry, true);
    } else if (err) {
      setMode(err.getAttribute('data-atp-err') === 'email' ? 'email' : 'phone');
      window.setTimeout(function () { join.scrollIntoView({ block: 'start' }); }, 200);
    }
    /* Returning visitors get the normal page, with a quiet link to their pass instead of landing on it. */
    var mine = $('[data-atp-mine]');
    if (mine && entry && !entry.pending && !ok) {
      mine.style.display = '';
      mine.addEventListener('click', function () { showDone(entry, true); });
    }

    var shareBtn = $('[data-atp-share]'), copyBtn = $('[data-atp-copy]'), again = $('[data-atp-again]');
    if (shareBtn) shareBtn.addEventListener('click', function () {
      var text = t('share_text').replace(/\[edition\]/g, eds[cur].code);
      var url = shareLink();
      if (navigator.share) {
        navigator.share({ title: document.title || 'ATHAR', text: text, url: url }).catch(function () {});
      } else {
        window.open('https://wa.me/?text=' + encodeURIComponent(text + ' ' + url), '_blank', 'noopener');
      }
    });
    if (copyBtn) copyBtn.addEventListener('click', function () {
      var url = shareLink(), label = t('copy') || copyBtn.textContent;
      var flash = function () {
        copyBtn.textContent = copyBtn.getAttribute('data-done') || label;
        window.setTimeout(function () { copyBtn.textContent = label; }, 2200);
      };
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(url).then(flash, function () { window.prompt('', url); });
      } else {
        var ta = document.createElement('textarea');
        ta.value = url; ta.setAttribute('readonly', ''); ta.style.position = 'fixed'; ta.style.opacity = '0';
        document.body.appendChild(ta); ta.select();
        try { document.execCommand('copy'); flash(); } catch (er) { /* ignore */ }
        document.body.removeChild(ta);
      }
    });
    if (again) again.addEventListener('click', function () {
      hideDone();
      if (tel) { tel.value = ''; tel.focus(); }
    });

    /* ---------- countdown ---------- */
    var countEl = $('[data-atp-count]');
    var opens = Date.parse(root.getAttribute('data-opens') || '');
    if (countEl && !isNaN(opens)) {
      var pad = function (n) { return (n < 10 ? '0' : '') + n; };
      var tick = function () {
        var s = Math.floor((opens - Date.now()) / 1000);
        if (s <= 0) { countEl.hidden = true; return; }
        var dd = Math.floor(s / 86400), hh = Math.floor(s % 86400 / 3600), mm = Math.floor(s % 3600 / 60), ss = s % 60;
        countEl.textContent = (dd ? dd + 'd ' : '') + pad(hh) + 'h ' + pad(mm) + 'm ' + pad(ss) + 's';
        countEl.hidden = false;
        window.setTimeout(tick, 1000);
      };
      tick();
    }

    /* ---------- mobile dock ---------- */
    var joinVisible = false;
    function updateDock() {
      if (!dock) return;
      var nearEnd = (window.innerHeight + window.scrollY) >= (document.documentElement.scrollHeight - 140);
      var on = window.innerWidth < 900 && !root.hasAttribute('data-done') && !joinVisible && !nearEnd;
      dock.classList.toggle('is-on', on);
      dock.setAttribute('aria-hidden', on ? 'false' : 'true');
      if (go) go.tabIndex = on ? 0 : -1;
    }
    if (window.IntersectionObserver && join) {
      new IntersectionObserver(function (en) { joinVisible = en[0].isIntersecting; updateDock(); }, { threshold: 0.25 }).observe(join);
    }
    window.addEventListener('scroll', updateDock, { passive: true });
    window.addEventListener('resize', updateDock);
    if (go) go.addEventListener('click', function () {
      var f = root.getAttribute('data-mode') === 'email' ? mail : tel;
      if (f) { try { f.focus({ preventScroll: true }); } catch (e) { f.focus(); } }
      join.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
    });
    updateDock();

    /* ---------- cookie settings (only when Shopify's cookie banner is on) ---------- */
    var cookieBtn = $('[data-atp-cookies]');
    if (cookieBtn) {
      var checkBanner = function () { if (window.privacyBanner && window.privacyBanner.showPreferences) cookieBtn.hidden = false; };
      window.addEventListener('load', function () { checkBanner(); window.setTimeout(checkBanner, 2500); });
      checkBanner();
      cookieBtn.addEventListener('click', function () {
        if (window.privacyBanner && window.privacyBanner.showPreferences) window.privacyBanner.showPreferences();
      });
    }
  }

  function boot() {
    var roots = document.querySelectorAll('[data-atp]');
    for (var i = 0; i < roots.length; i++) init(roots[i]);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
